import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  useNavigate,
  useSearchParams,
  useLocation,
  useParams,
} from "react-router-dom";
import ReactQuill from "react-quill";
import { Box, Stack, TextField, Typography } from "@mui/material";
import "react-quill/dist/quill.snow.css";
import {
  Plus,
  Paperclip,
  Trash2,
  ArrowUp,
  ArrowDown,
  X,
  Upload,
  ArrowLeft,
  GripVertical,
  History,
} from "lucide-react";
import { logger } from "../services/logService";
import s3UploadService, {
  extractBase64Images,
  base64ToFile,
} from "../services/s3UploadService";
import sharedStepService from "../services/sharedStepService";
import sectionService from "../services/sectionService";
import { suiteService } from "../services/suiteService";
import { branchService } from "../services/branchService";
import { projectService } from "../services/projectService";
import { testcaseService } from "../services/testcaseService";
import { useAuth } from "../context/AuthContext";
import { getStoredClientId } from "../utils/tokenUtils";
import { setLastTestCaseForHistory } from "../utils/testCaseHistoryNav";
import toast from "react-hot-toast";
import { teststepsService } from "../services/teststepsService";
import { addComment, getComments } from "../services/testCaseCommentService";
import FormSkeleton from "../components/skeletons/FormSkeleton";

const TestCaseCreatePage = ({ viewMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project_id");
  const defaultSectionId = searchParams.get("section_id");
  const branchId = searchParams.get("branch_id");
  const suiteId = searchParams.get("suite_id"); // Get suite_id from URL (for MULTIPLE_TEST_SUITES)
  // Prefer query (?test_case_id=) for legacy links; use path /test-cases/view/:testCaseId (e.g. from Defects).
  const testCaseIdFromPath = params.testCaseId
    ? (() => {
      try {
        return decodeURIComponent(params.testCaseId);
      } catch {
        return params.testCaseId;
      }
    })()
    : null;
  const testCaseId = searchParams.get("test_case_id") || testCaseIdFromPath;
  const [savedComments, setSavedComments] = useState([]);

  // Store suite_id in sessionStorage when component mounts to preserve it on browser back
  useEffect(() => {
    if (suiteId) {
      sessionStorage.setItem(`testCaseCreate_suiteId_${projectId}`, suiteId);
    }

    // Cleanup: restore suite_id when component unmounts (browser back)
    return () => {
      // This runs when navigating away
      // If suite_id exists, ensure it's preserved
      if (suiteId) {
        sessionStorage.setItem(`testCaseCreate_suiteId_${projectId}`, suiteId);
      }
    };
  }, [suiteId, projectId]);

  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false); // Track edit mode when in viewMode
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "medium",
    status: "active",
    preconditions: "",
    test_case_structure_type: "MULTIPLE_STEPS",
    section_id: defaultSectionId || null,
    suite_id: suiteId || null, // Initialize from URL params
    branch_id: branchId || null,
    owner: "",
    assigned_to: user?.user_id || user?.id || "", // default to current user
    reviewer_id: "",
    state: "active",
    type_of_test_case: "other",
    automation_status: "not_automated",
    automation_type: null, // Only for BDD: 'record_and_play' or 'self_automated'
    tags: "",
    attachments: [],
    comments: "", // Comments field for test case creation
    testSteps: [{ step: "", expectedResult: "", gherkin_keyword: "" }],
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sharedSteps, setSharedSteps] = useState([]);

  const [showSharedStepsModal, setShowSharedStepsModal] = useState(false);
  const [sharedStepInsertIndex, setSharedStepInsertIndex] = useState(null);
  const [loadingSharedSteps, setLoadingSharedSteps] = useState(false);
  const [projectUsers, setProjectUsers] = useState([]);
  const [loadingProjectUsers, setLoadingProjectUsers] = useState(false);
  const [projectStructure, setProjectStructure] = useState(null);
  const [loadingProjectStructure, setLoadingProjectStructure] = useState(false);
  const [suites, setSuites] = useState([]);
  const [loadingSuites, setLoadingSuites] = useState(false);
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [projectSettings, setProjectSettings] = useState({
    ai_test_case_generation: false,
    test_case_approval: false,
  });
  const [testCaseTemplateType, setTestCaseTemplateType] =
    useState("MULTIPLE_STEPS"); // SINGLE_STEP, MULTIPLE_STEPS, BDD
  const [draggedStepIndex, setDraggedStepIndex] = useState(null);
  const [dragOverStepIndex, setDragOverStepIndex] = useState(null);

  const getCommentsData = async () => {
    const comments = await getComments(testCaseId);
    setSavedComments(comments?.data?.comments || []);
  };
  const getTestCaseData = async () => {
    if (!testCaseId || !projectId) return;
    try {
      const response = await testcaseService.getTestCaseById(
        projectId,
        testCaseId,
      );
      const testCaseData = response?.data || response;
      if (!testCaseData || typeof testCaseData !== "object") return;

      const stepsResponse = await teststepsService.getTestSteps(testCaseId);

      let steps = [];
      if (stepsResponse?.data?.test_steps) {
        steps = stepsResponse.data.test_steps;
      } else if (stepsResponse?.test_steps) {
        steps = stepsResponse.test_steps;
      } else if (Array.isArray(stepsResponse?.data)) {
        steps = stepsResponse.data;
      } else if (Array.isArray(stepsResponse)) {
        steps = stepsResponse;
      }

      const structureType =
        testCaseData.test_case_structure_type || "MULTIPLE_STEPS";
      setTestCaseTemplateType(structureType);

      const mappedSteps = (steps || []).map((item) => ({
        step: item.action ?? "",
        expectedResult: item.expected_result ?? "",
        shared_step_id: item.shared_step_id ?? "",
        isSharedStep: !!item.shared_step_id,
        gherkin_keyword:
          item.gherkin_keyword || (structureType === "BDD" ? "Given" : ""),
      }));

      setFormData((prev) => ({
        ...prev,
        ...testCaseData,
        preconditions: testCaseData.preconditions ?? prev.preconditions ?? "",
        description: testCaseData.description ?? prev.description ?? "",
        attachments: Array.isArray(testCaseData.attachments)
          ? testCaseData.attachments
          : prev.attachments || [],
        test_case_structure_type: structureType,
        testSteps:
          mappedSteps.length > 0
            ? mappedSteps
            : [
              {
                step: "",
                expectedResult: "",
                gherkin_keyword: structureType === "BDD" ? "Given" : "",
              },
            ],
      }));
      setIsEditing(false);
    } catch (e) {
      logger.error(e, "Failed to load test case");
      toast.error(e?.message || "Failed to load test case");
    }
  };
  useEffect(() => {
    if (testCaseId && projectId) {
      getTestCaseData();
      getCommentsData();
    }
  }, [testCaseId, projectId]);
  // Rich text editor modules
  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      ["link", "image", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ table: true }],
      ["clean"],
    ],
  };

  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "link",
    "image",
    "code-block",
    "list",
    "bullet",
    "table",
  ];

  // Load sections
  const loadSections = useCallback(async () => {
    try {
      setLoadingSections(true);
      const response = await sectionService.getSectionTree(projectId);
      const sectionTree = response?.data?.sections || response?.sections || [];
      setSections(sectionTree);

      // Helper to flatten tree for searching and counting
      const flatten = (list) => {
        let result = [];
        list.forEach((section) => {
          if (!section || !section.section_id) return;
          result.push(section);
          if (section.children && section.children.length > 0) {
            result = result.concat(flatten(section.children));
          }
        });
        return result;
      };

      const allSections = flatten(sectionTree);
      let targetSectionId = null;

      // Priority 1: Check URL param (defaultSectionId) - this handles section creation flow
      if (defaultSectionId) {
        // Use loose comparison or string conversion for IDs
        const found = allSections.find(
          (s) => String(s.section_id) === String(defaultSectionId),
        );
        if (found) {
          targetSectionId = found.section_id;
        }
      }

      // Priority 2: Check sessionStorage for recently created section
      if (!targetSectionId) {
        const recentSectionId = sessionStorage.getItem(
          `recentlyCreatedSection_${projectId}`,
        );
        if (recentSectionId) {
          const found = allSections.find(
            (s) => String(s.section_id) === String(recentSectionId),
          );
          if (found) {
            targetSectionId = found.section_id;
            // Clear the stored value after use
            sessionStorage.removeItem(`recentlyCreatedSection_${projectId}`);
          }
        }
      }

      // Priority 3: Single Section Auto-Selection
      // If NO target selected yet AND there is exactly one section in the project
      if (!targetSectionId && allSections.length === 1) {
        targetSectionId = allSections[0].section_id;
      }

      // Apply selection if valid
      if (targetSectionId) {
        setFormData((prev) => ({
          ...prev,
          section_id: targetSectionId,
        }));
      }
    } catch (error) {
      logger.error(error, "Failed to load sections");
      toast.error("Failed to load sections");
    } finally {
      setLoadingSections(false);
    }
  }, [projectId, defaultSectionId]);

  // Load assignable users (all active client users for Assign To / Owner dropdowns)
  const loadProjectUsers = useCallback(async () => {
    try {
      setLoadingProjectUsers(true);
      const response = await projectService.getAssignableUsers(projectId);
      setProjectUsers(response?.data?.users || response?.users || []);
    } catch (error) {
      logger.error(error, "Failed to load assignable users");
      toast.error("Failed to load assignable users");
    } finally {
      setLoadingProjectUsers(false);
    }
  }, [projectId]);

  // Load project structure
  const loadProjectStructure = useCallback(async () => {
    try {
      setLoadingProjectStructure(true);
      const response = await projectService.getProject(projectId);
      const project = response.data?.project || response.data || {};
      const structure = project.project_type || "SINGLE_REPOSITORY";
      setProjectStructure(structure);

      // Store project feature toggles
      setProjectSettings({
        ai_test_case_generation:
          project.ai_test_case_generation === true ||
          project.ai_test_case_generation === "true",
        test_case_approval:
          project.test_case_approval === true ||
          project.test_case_approval === "true",
      });

      // Template defaults come from the project only when creating a new test case.
      // When viewing/editing an existing case, getTestCaseData sets template + steps;
      // doing this here would race and wipe loaded steps (notably for SINGLE_STEP).
      const templateType = project.test_case_structure_type || "MULTIPLE_STEPS";
      if (!testCaseId) {
        setTestCaseTemplateType(templateType);

        if (templateType === "SINGLE_STEP") {
          setFormData((prev) => ({
            ...prev,
            test_case_structure_type: "SINGLE_STEP",
            testSteps: [{ step: "", expectedResult: "", gherkin_keyword: "" }],
          }));
        } else if (templateType === "BDD") {
          setFormData((prev) => ({
            ...prev,
            test_case_structure_type: "BDD",
            testSteps: [
              { step: "", expectedResult: "", gherkin_keyword: "Given" },
            ],
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            test_case_structure_type: "MULTIPLE_STEPS",
            testSteps:
              prev.testSteps?.length > 0
                ? prev.testSteps
                : [{ step: "", expectedResult: "", gherkin_keyword: "" }],
          }));
        }
      }
    } catch (error) {
      logger.error(error, "Failed to load project structure");
      toast.error("Failed to load project structure");
    } finally {
      setLoadingProjectStructure(false);
    }
  }, [projectId, testCaseId]);

  // Load shared steps
  const loadSharedSteps = useCallback(async () => {
    try {
      setLoadingSharedSteps(true);
      const response = await sharedStepService.getSharedSteps(projectId);
      setSharedSteps(response.data?.shared_steps || []);
    } catch (error) {
      logger.error(error, "Failed to load shared steps");
    } finally {
      setLoadingSharedSteps(false);
    }
  }, [projectId]);

  // Load all data on mount
  useEffect(() => {
    if (projectId) {
      loadSections();
      loadProjectUsers();
      loadProjectStructure();
      loadSharedSteps();
    }
  }, [
    projectId,
    loadSections,
    loadProjectUsers,
    loadProjectStructure,
    loadSharedSteps,
  ]);

  // Default Assign To to current user (creator) so their name is shown selected in the dropdown
  const currentUserId = user?.user_id || user?.id || "";
  useEffect(() => {
    if (!currentUserId) return;
    setFormData((prev) => {
      if (prev.assigned_to !== "" && prev.assigned_to != null) return prev;
      return { ...prev, assigned_to: currentUserId };
    });
  }, [currentUserId]);

  // When assignable users finish loading, default Assign To to current user if still empty
  useEffect(() => {
    if (!currentUserId || projectUsers.length === 0) return;
    const currentUserInList = projectUsers.some(
      (u) => (u.user_id || u.id) === currentUserId,
    );
    if (!currentUserInList) return;
    setFormData((prev) => {
      if (prev.assigned_to !== "" && prev.assigned_to != null) return prev;
      return { ...prev, assigned_to: currentUserId };
    });
  }, [currentUserId, projectUsers]);

  // Include current user (e.g. chief admin) in Assign To dropdown if not already in project users list
  const assignableUsersWithCurrent = useMemo(() => {
    const list = projectUsers || [];
    const uid = user?.user_id || user?.id;
    if (!uid) return list;
    const alreadyInList = list.some((u) => (u.user_id || u.id) === uid);
    if (alreadyInList) return list;
    return [
      {
        user_id: uid,
        full_name: user?.full_name || user?.email || "Me",
        email: user?.email || "",
      },
      ...list,
    ];
  }, [user, projectUsers]);

  // Flatten sections for dropdown
  const flattenedSections = useMemo(() => {
    const flatten = (sectionList, level = 0) => {
      let result = [];
      sectionList.forEach((section) => {
        if (!section || !section.section_id) return;
        const indent = "  ".repeat(level);
        result.push({
          value: section.section_id,
          label: `${indent}${section.name}`,
          section,
        });
        if (section.children && section.children.length > 0) {
          result = result.concat(flatten(section.children, level + 1));
        }
      });
      return result;
    };
    return flatten(sections);
  }, [sections]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle test step changes
  const handleStepChange = (index, field, value) => {
    setFormData((prev) => {
      const newSteps = [...prev.testSteps];
      newSteps[index] = { ...newSteps[index], [field]: value };
      return { ...prev, testSteps: newSteps };
    });
  };

  // Add test step
  const addStep = () => {
    // Prevent adding steps for SINGLE_STEP template
    if (testCaseTemplateType === "SINGLE_STEP") {
      toast.error("SINGLE_STEP template type allows only one step");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      testSteps: [
        ...prev.testSteps,
        {
          step: "",
          expectedResult: testCaseTemplateType === "BDD" ? "" : "",
          gherkin_keyword: testCaseTemplateType === "BDD" ? "Given" : "",
        },
      ],
    }));
  };

  // Remove test step
  const removeStep = (index) => {
    // Prevent removing steps for SINGLE_STEP template
    if (testCaseTemplateType === "SINGLE_STEP") {
      toast.error("SINGLE_STEP template type requires exactly one step");
      return;
    }

    if (formData.testSteps?.length === 1) {
      toast.error("At least one step is required");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      testSteps: prev.testSteps.filter((_, i) => i !== index),
    }));
  };

  console.log({ formData });

  // Handle save
  const handleSave = async () => {
    if (!testCaseId || !projectId) return;

    // Validate approver if test_case_approval is enabled
    if (projectSettings.test_case_approval && !formData.reviewer_id) {
      toast.error("Approver is required for this project");
      return;
    }

    // Validate steps based on template type
    if (testCaseTemplateType === "SINGLE_STEP") {
      if (formData.testSteps.length !== 1) {
        toast.error("SINGLE_STEP template requires exactly one step");
        return;
      }
      const step = formData.testSteps[0];
      if (!step.step?.trim()) {
        toast.error("Step description is required");
        return;
      }
      if (!step.expectedResult?.trim()) {
        toast.error("Expected result is required");
        return;
      }
    } else if (testCaseTemplateType === "MULTIPLE_STEPS") {
      if (formData.testSteps.length === 0) {
        toast.error("At least one step is required");
        return;
      }
      for (let i = 0; i < formData.testSteps.length; i++) {
        const step = formData.testSteps[i];
        if (!step.step?.trim()) {
          toast.error(`Step ${i + 1} description is required`);
          return;
        }
        if (!step.expectedResult?.trim()) {
          toast.error(`Step ${i + 1} expected result is required`);
          return;
        }
      }
    } else if (testCaseTemplateType === "BDD") {
      if (formData.testSteps.length === 0) {
        toast.error("At least one Gherkin step is required");
        return;
      }
      let hasGiven = false;
      let hasWhen = false;
      let hasThen = false;

      for (let i = 0; i < formData.testSteps.length; i++) {
        const step = formData.testSteps[i];
        if (!step.step?.trim()) {
          toast.error(`Step ${i + 1} must have content`);
          return;
        }
        if (!step.gherkin_keyword) {
          toast.error(`Step ${i + 1} must have a Gherkin keyword`);
          return;
        }

        const keyword = step.gherkin_keyword;
        if (keyword === "Given" || (keyword === "And" && i === 0))
          hasGiven = true;
        if (keyword === "When" || (keyword === "And" && hasGiven && !hasWhen))
          hasWhen = true;
        if (keyword === "Then" || (keyword === "And" && hasWhen && !hasThen))
          hasThen = true;
      }

      if (!hasGiven) {
        toast.error("BDD template must contain at least one Given clause");
        return;
      }
      if (!hasWhen) {
        toast.error("BDD template must contain at least one When clause");
        return;
      }
      if (!hasThen) {
        toast.error("BDD template must contain at least one Then clause");
        return;
      }
    }

    setSaving(true);
    try {
      const clientId = getStoredClientId();

      // Extract and upload base64 images from preconditions
      const preconditionsImages = extractBase64Images(
        formData.preconditions || "",
      );
      let preconditionsText = formData.preconditions || "";

      for (const img of preconditionsImages) {
        try {
          const file = base64ToFile(img.data, img.fileName, img.mimeType);
          const url = await s3UploadService.uploadFile(
            file,
            "test-cases",
            clientId,
            projectId,
          );
          preconditionsText = preconditionsText.replace(img.fullDataUrl, url);
        } catch (uploadError) {
          logger.warn(uploadError, "Failed to upload image, keeping base64");
        }
      }

      // Process test steps based on template type
      const processedSteps = await Promise.all(
        formData.testSteps.map(async (step) => {
          const stepImages = extractBase64Images(step.step || "");
          const resultImages =
            testCaseTemplateType !== "BDD"
              ? extractBase64Images(step.expectedResult || "")
              : [];

          let stepText = step.step || "";
          let resultText =
            testCaseTemplateType !== "BDD" ? step.expectedResult || "" : "";

          // Upload step images
          for (const img of stepImages) {
            try {
              const file = base64ToFile(img.data, img.fileName, img.mimeType);
              const url = await s3UploadService.uploadFile(
                file,
                "test-steps",
                clientId,
                projectId,
              );
              stepText = stepText.replace(img.fullDataUrl, url);
            } catch (uploadError) {
              logger.warn(
                uploadError,
                "Failed to upload step image, keeping base64",
              );
            }
          }

          // Upload result images (only for non-BDD)
          if (testCaseTemplateType !== "BDD") {
            for (const img of resultImages) {
              try {
                const file = base64ToFile(img.data, img.fileName, img.mimeType);
                const url = await s3UploadService.uploadFile(
                  file,
                  "test-steps",
                  clientId,
                  projectId,
                );
                resultText = resultText.replace(img.fullDataUrl, url);
              } catch (uploadError) {
                logger.warn(
                  uploadError,
                  "Failed to upload result image, keeping base64",
                );
              }
            }
          }

          // For BDD, prepend Gherkin keyword to step text if not already present
          if (testCaseTemplateType === "BDD") {
            const keyword = step.gherkin_keyword || "Given";
            if (!stepText.trim().startsWith(keyword)) {
              stepText = `${keyword} ${stepText.trim()}`;
            }
            // BDD should not have expectedResult field
            resultText = null;
          }

          return {
            step: stepText,
            expectedResult: resultText,
            gherkin_keyword:
              testCaseTemplateType === "BDD"
                ? step.gherkin_keyword || "Given"
                : null,
          };
        }),
      );

      const payload = {
        title: formData.title,
        preconditions: preconditionsText,
        priority: formData.priority,
        type_of_test_case: formData.type_of_test_case,
        automation_status: formData.automation_status,
        automation_type: formData.automation_type || null,
        reviewer_id: formData.reviewer_id || null,
        folder_id: formData.section_id,
        attachments: formData.attachments, // Include attachments in update
        test_case_structure_type: testCaseTemplateType, // Ensure correct template type is sent
        testSteps: processedSteps,
        steps: processedSteps
          .filter((step) => step.step.trim() !== "")
          .map((step, index) => ({
            step_number: index + 1,
            action: step.step,
            expected_result: step.expectedResult || null,
            gherkin_keyword: step.gherkin_keyword || null,
          })),
      };

      await testcaseService.updateTestCase(projectId, testCaseId, payload);
      toast.success("Test case updated successfully");
      getTestCaseData();
    } catch (error) {
      logger.error(error, "Failed to update test case");
      toast.error(error.message || "Failed to update test case");
    } finally {
      setSaving(false);
    }
  };
  // Move step up
  const moveStepUp = (index) => {
    if (index === 0) return;
    setFormData((prev) => {
      const newSteps = [...prev.testSteps];
      [newSteps[index - 1], newSteps[index]] = [
        newSteps[index],
        newSteps[index - 1],
      ];
      return { ...prev, testSteps: newSteps };
    });
  };

  // Move step down
  const moveStepDown = (index) => {
    if (index === formData.testSteps.length - 1) return;
    setFormData((prev) => {
      const newSteps = [...prev.testSteps];
      [newSteps[index], newSteps[index + 1]] = [
        newSteps[index + 1],
        newSteps[index],
      ];
      return { ...prev, testSteps: newSteps };
    });
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedStepIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.currentTarget);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStepIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverStepIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedStepIndex === null || draggedStepIndex === dropIndex) {
      setDraggedStepIndex(null);
      setDragOverStepIndex(null);
      return;
    }

    setFormData((prev) => {
      const newSteps = [...prev.testSteps];
      const draggedStep = newSteps[draggedStepIndex];

      // Remove from old position
      newSteps.splice(draggedStepIndex, 1);

      // Insert at new position
      newSteps.splice(dropIndex, 0, draggedStep);

      return { ...prev, testSteps: newSteps };
    });

    setDraggedStepIndex(null);
    setDragOverStepIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedStepIndex(null);
    setDragOverStepIndex(null);
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const clientId = getStoredClientId();
      const uploadedFiles = await Promise.all(
        files.map(async (file) => {
          const response = await s3UploadService.uploadFile(
            file,
            "test-cases",
            clientId,
            projectId,
          );
          return {
            file_name: file.name,
            file_url: response.fileUrl || response,
            file_size: file.size,
            file_type: file.type,
          };
        }),
      );

      setFormData((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedFiles],
      }));
      toast.success(`${files.length} file(s) uploaded successfully`);
    } catch (error) {
      logger.error(error, "Failed to upload files");
      toast.error("Failed to upload files");
    } finally {
      setUploading(false);
    }
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const addCommentData = async () => {
    await addComment(testCaseId, formData.comments, null);
    setFormData({ ...formData, comments: "" });
    getCommentsData();
  };

  const handleInsertSharedStep = (sharedStep, index) => {
    setFormData((prev) => ({
      ...prev,
      testSteps: [
        ...prev.testSteps.slice(0, index),
        {
          step: sharedStep.step_description,
          expectedResult: sharedStep.expected_result || "",
          isSharedStep: true,
          sharedStepId: sharedStep.shared_step_id,
        },
        ...prev.testSteps.slice(index),
      ],
    }));
    setShowSharedStepsModal(false);
    setSharedStepInsertIndex(null);
    toast.success("Shared step inserted successfully!");
  };
  console.log({formData})
  const handleAddSharedStep = (sharedStep) => {
    const step=formData.testSteps[0]
    setFormData((prev) => ({
      ...prev,
      testSteps: [

        {
          ...step,
          step: step.step+"<br/>"+sharedStep.step_description,
          expectedResult: (step.expectedResult?(step.expectedResult+"<br/>"):"")+(sharedStep.expected_result || ""),
        },
      ],
    }));
    setShowSharedStepsModal(false);
    setSharedStepInsertIndex(null);
    toast.success("Shared step added successfully!");
  }

  // Validation
  const validate = () => {
    const newErrors = {};
    const errorMessages = [];

    // Validate approver if test_case_approval is enabled
    if (projectSettings.test_case_approval && !formData.reviewer_id) {
      newErrors.reviewer_id = "Approver is required for this project";
      errorMessages.push("Approver is required for this project");
    }

    // Title is mandatory
    if (!formData.title?.trim()) {
      newErrors.title = "Title is required";
      errorMessages.push("Title is required");
    }

    // Section is mandatory
    if (!formData.section_id) {
      newErrors.section_id = "Section is required";
      errorMessages.push("Section is required");
    }

    // Steps and Expected Result are now optional - no validation needed
    // Only validate BDD structure if steps are provided
    if (testCaseTemplateType === "BDD" && formData.testSteps.length > 0) {
      let hasGiven = false;
      let hasWhen = false;
      let hasThen = false;

      formData.testSteps.forEach((step, index) => {
        if (step.step?.trim() && !step.gherkin_keyword) {
          newErrors.testSteps = `Step ${index + 1} must have a Gherkin keyword`;
          errorMessages.push(`Step ${index + 1} must have a Gherkin keyword`);
        }

        const keyword = step.gherkin_keyword;
        if (keyword === "Given" || (keyword === "And" && index === 0))
          hasGiven = true;
        if (keyword === "When" || (keyword === "And" && hasGiven && !hasWhen))
          hasWhen = true;
        if (keyword === "Then" || (keyword === "And" && hasWhen && !hasThen))
          hasThen = true;
      });

      if (formData.testSteps.some((s) => s.step?.trim()) && !hasGiven) {
        newErrors.testSteps =
          "BDD template must contain at least one Given clause";
        errorMessages.push(
          "BDD template must contain at least one Given clause",
        );
      }
      if (formData.testSteps.some((s) => s.step?.trim()) && !hasWhen) {
        newErrors.testSteps =
          "BDD template must contain at least one When clause";
        errorMessages.push(
          "BDD template must contain at least one When clause",
        );
      }
      if (formData.testSteps.some((s) => s.step?.trim()) && !hasThen) {
        newErrors.testSteps =
          "BDD template must contain at least one Then clause";
        errorMessages.push(
          "BDD template must contain at least one Then clause",
        );
      }
    }

    // Store error messages for display
    if (errorMessages.length > 0) {
      newErrors._messages = errorMessages;
    }

    return newErrors;
  };


  const approvers = useMemo(() => {

    return projectUsers
      .filter((projectUser) => {
        const currentUserId = user?.user_id || user?.id;
        const currentUserEmail = user?.email;
        return (
          projectUser.user_id !== currentUserId &&
          projectUser.email !== currentUserEmail
        );
      })
  }, [projectUsers])


  useEffect(() => {
    if (projectSettings.test_case_approval) {

      console.log({ asjdnjkasdn: 123, approvers })
      setFormData({ ...formData, reviewer_id: approvers[0]?.user_id })
    }
  }, [projectUsers])

  // Handle submit
  const handleSubmit = async (createMore = false) => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Show specific error messages
      if (validationErrors._messages && validationErrors._messages.length > 0) {
        validationErrors._messages.forEach((msg) => toast.error(msg));
      } else {
        toast.error("Please fix validation errors");
      }
      return;
    }

    setLoading(true);
    try {
      // Extract base64 images from rich text fields
      const preconditionsImages = await extractBase64Images(
        formData.preconditions || "",
        projectId,
      );
      let preconditionsText = formData.preconditions || "";
      preconditionsImages.forEach((img, index) => {
        preconditionsText = preconditionsText.replace(img.base64, img.url);
      });

      // Process test steps based on template type
      const processedSteps = await Promise.all(
        formData.testSteps.map(async (step) => {
          const stepImages = await extractBase64Images(
            step.step || "",
            projectId,
          );
          const resultImages =
            testCaseTemplateType !== "BDD"
              ? await extractBase64Images(step.expectedResult || "", projectId)
              : [];

          let stepText = step.step || "";
          let resultText =
            testCaseTemplateType !== "BDD" ? step.expectedResult || "" : "";

          stepImages.forEach((img) => {
            stepText = stepText.replace(img.base64, img.url);
          });

          if (testCaseTemplateType !== "BDD") {
            resultImages.forEach((img) => {
              resultText = resultText.replace(img.base64, img.url);
            });
          }

          // For BDD, prepend Gherkin keyword to step text if not already present
          if (testCaseTemplateType === "BDD") {
            const keyword = step.gherkin_keyword || "Given";
            if (!stepText.trim().startsWith(keyword)) {
              stepText = `${keyword} ${stepText.trim()}`;
            }
            // BDD should not have expectedResult field
            resultText = null;
          }

          return {
            step: stepText,
            expectedResult: resultText,
            gherkin_keyword:
              testCaseTemplateType === "BDD"
                ? step.gherkin_keyword || "Given"
                : null,
            isSharedStep: step.isSharedStep || null,
            sharedStepId: step.sharedStepId || null,
          };
        }),
      );

      const payload = {
        ...formData,
        preconditions: preconditionsText,
        testSteps: processedSteps,
        test_case_structure_type: testCaseTemplateType, // Ensure correct template type is sent
        folder_id: formData.section_id, // Map section_id to folder_id for API
        branch_id: formData.branch_id || branchId || null, // Ensure branch_id is included
        tags: formData.tags
          ? formData.tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
          : [],
        // Extract comments to save separately after test case creation
        comment: formData.comments ? formData.comments.trim() : null,
        // Format steps for backend API
        steps: processedSteps.map((step, index) => ({
          step_number: index + 1,
          action: step.step,
          expected_result: step.expectedResult || null,
          gherkin_keyword: step.gherkin_keyword || null,
          isSharedStep: step.isSharedStep || null,
          sharedStepId: step.sharedStepId || null,
        })),
      };

      // Debug log to verify branch_id and comments are being sent
      console.log("Creating test case with payload:", {
        ...payload,
        testSteps: "[...]",
        hasComment: !!payload.comment,
      });

      await testcaseService.createTestCase(projectId, payload);
      toast.success("Test case created successfully");

      if (createMore) {
        // Reset form but preserve branch_id and section_id
        setFormData({
          ...formData,
          title: "",
          description: "",
          preconditions: "",
          comments: "", // Reset comments field
          testSteps:
            testCaseTemplateType === "SINGLE_STEP"
              ? [{ step: "", expectedResult: "", gherkin_keyword: "" }]
              : testCaseTemplateType === "BDD"
                ? [{ step: "", expectedResult: "", gherkin_keyword: "Given" }]
                : [{ step: "", expectedResult: "", gherkin_keyword: "" }],
          attachments: [],
        });
        setErrors({});
      } else {
        // Navigate back - preserve branch_id, suite_id, and other URL params
        const params = new URLSearchParams({ project_id: projectId });
        if (branchId) {
          params.append("branch_id", branchId);
        }
        if (suiteId) {
          params.append("suite_id", suiteId);
        }
        // Preserve other important params
        const projectName = searchParams.get("project_name");
        const clientId = searchParams.get("client_id");
        if (projectName) {
          params.append("project_name", projectName);
        }
        if (clientId) {
          params.append("client_id", clientId);
        }
        // Clear sessionStorage since we're navigating programmatically
        if (projectId) {
          sessionStorage.removeItem(`testCaseCreate_suiteId_${projectId}`);
        }
        navigate(`/test-cases?${params.toString()}`);
      }
    } catch (error) {
      logger.error(error, "Failed to create test case");
      toast.error(error.message || "Failed to create test case");
    } finally {
      setLoading(false);
    }
  };

  const SharedStepsSelectionModal = ({
    isOpen,
    onClose,
    sharedSteps,
    loading,
    onSelect,
  }) => {
    const [searchTerm, setSearchTerm] = useState("");

    if (!isOpen) return null;

    const filteredSteps = sharedSteps.filter((step) => {
      const description = step.step_description || "";
      const expectedResult = step.expected_result || "";
      const searchLower = searchTerm.toLowerCase();
      return (
        description.toLowerCase().includes(searchLower) ||
        expectedResult.toLowerCase().includes(searchLower)
      );
    });

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-50"
        onClick={onClose}
        style={{ zIndex: 9999 }}
      >
        <div
          className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Select Shared Step</h2>
            <button
              onClick={onClose}
              className="text-text-tertiary hover:text-text-secondary"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search shared steps..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              className="w-full px-4 py-2 bg-bg-primary text-text-primary border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder:text-text-tertiary"
            />
          </div>

          {/* Shared Steps List */}
          {loading ? (
            <div className="py-6 px-2">
              <FormSkeleton fields={5} className="max-w-none mx-0 p-0" />
            </div>
          ) : filteredSteps.length === 0 ? (
            <div className="text-center py-12 text-text-tertiary">
              {searchTerm
                ? "No shared steps found matching your search."
                : "No shared steps available. Create one first."}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSteps.map((step) => (
                <div
                  key={step.shared_step_id}
                  className="border border-border-primary rounded-lg p-2 hover:bg-bg-hover cursor-pointer transition-colors"
                  onClick={() => onSelect(step)}
                >
                  <div className="flex items-start justify-between " style={{ alignItems: "center" }}>
                    <div className="flex-1">
                      <div
                        className="text-sm font-medium text-text-primary mb-2"
                        dangerouslySetInnerHTML={{
                          __html:
                            step.step_description?.substring(0, 200) +
                            (step.step_description?.length > 200
                              ? "..."
                              : "") || "No description",
                        }}
                      />

                      <div
                        className="text-xs text-text-secondary"
                        dangerouslySetInnerHTML={{
                          __html:
                            step.expected_result ?
                              step.expected_result?.substring(0, 150) +
                              (step.expected_result?.length > 150 ? "..." : "") : "<div>-</div>",
                        }}
                      />

                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(step);
                      }}
                      className="ml-4 px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      Insert
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleCancel = () => {
    // Preserve branch_id, suite_id, and other URL params
    const params = new URLSearchParams({ project_id: projectId });
    if (branchId) {
      params.append("branch_id", branchId);
    }
    if (suiteId) {
      params.append("suite_id", suiteId);
    }
    // Preserve other important params
    const projectName = searchParams.get("project_name");
    const clientId = searchParams.get("client_id");
    if (projectName) {
      params.append("project_name", projectName);
    }
    if (clientId) {
      params.append("client_id", clientId);
    }
    // Clear sessionStorage since we're navigating programmatically
    if (projectId) {
      sessionStorage.removeItem(`testCaseCreate_suiteId_${projectId}`);
    }
    navigate(`/test-cases?${params.toString()}`);
  };

  // Determine if fields should be disabled
  const fieldsDisabled = viewMode && !isEditing;

  if (!projectId) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600">
            Project ID is required
          </h2>
          <button
            onClick={() => navigate("/projects")}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Go to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Save/Cancel buttons */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <span className="text-sm text-gray-600">
                {viewMode ? "View Test Case" : "Create Test Case"}
              </span>
            </div>
            <button
              onClick={() => {
                if (testCaseId) setLastTestCaseForHistory(testCaseId);
                navigate(
                  `/test-cases/history/${testCaseId}${window.location.search}`,
                );
              }}
              className="p-2 hover:bg-gray-100 rounded"
              style={{
                gap: "4px",
                display: "flex",
                border: "1px solid",
              }}
            >
              <History className="w-5 h-5 text-gray-600" />
              <span className="text-sm text-gray-600">View History</span>
            </button>
            {/* <div className="flex items-center gap-3"> */}
            {/* {viewMode ? (
                // View Mode Buttons: Edit or Cancel/Save
                <>
                  {!isEditing ? (
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Edit
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditing(false);
                          // Optionally reload original data here
                        }}
                        disabled={loading}
                        className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSave(false)}
                        disabled={loading}
                        className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                      >
                        {loading ? 'Saving...' : 'Save'}
                      </button>
                    </>
                  )}
                </>
              ) : (
                // Create Mode Buttons
                <>
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={loading}
                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSubmit(false)}
                    disabled={loading}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                </>
              )} */}
            {/* </div> */}
          </div>
        </div>
      </div>
      {showSharedStepsModal && (
        <SharedStepsSelectionModal
          isOpen={showSharedStepsModal}
          onClose={() => {
            setShowSharedStepsModal(false);
            setSharedStepInsertIndex(null);
          }}
          sharedSteps={sharedSteps}
          loading={loadingSharedSteps}
          onSelect={(sharedStep) => {
            if (testCaseTemplateType === "SINGLE_STEP") {
              handleAddSharedStep(sharedStep);
            }
            else if (sharedStepInsertIndex !== null) {
              handleInsertSharedStep(sharedStep, sharedStepInsertIndex);
            }
            // hereer
          }}
        />
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex gap-6">
          {/* Left Column - Form Fields */}
          <div className="flex-1 space-y-6">
            {/* Title */}
            {fieldsDisabled ? (
              <Stack flexDirection={"row"} gap={"6px"}>
                <Typography
                  sx={{
                    wordWrap: "pre",
                    background: "var(--color-gray-500)",
                    borderRadius: "8px",
                    color: "white",
                    padding: "0px 10px",
                  }}
                >
                  {testCaseId}
                </Typography>
                <Typography>{formData.title}</Typography>
              </Stack>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter title for your test case..."
                  disabled={fieldsDisabled}
                  className={`w-full px-3 py-2 text-sm border ${errors.title ? "border-red-500" : "border-gray-300"
                    } rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${fieldsDisabled ? "bg-gray-50 cursor-not-allowed" : ""}`}
                />
                {errors.title && (
                  <p className="text-red-500 text-xs mt-1">{errors.title}</p>
                )}
              </div>
            )}

            {/* Preconditions */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Preconditions
              </label>
              <div
                className={`border border-gray-300 rounded overflow-hidden ${fieldsDisabled ? "bg-gray-50" : ""}`}
              >
                <ReactQuill
                  value={formData.preconditions}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, preconditions: value }))
                  }
                  modules={quillModules}
                  formats={quillFormats}
                  className="min-h-[100px]"
                  placeholder="Define any preconditions about the test"
                  readOnly={fieldsDisabled}
                />
              </div>
            </div>

            {/* Steps */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Steps
              </label>

              {fieldsDisabled ? (
                /* View mode: plain two-column rows */
                <div className="space-y-3">
                  {formData?.testSteps?.map((step, index) => (
                    <div key={index} className="flex items-start gap-3">
                      {/* Step number badge */}
                      <div className="flex-shrink-0 mt-2">
                        <div className="w-6 h-6 flex items-center justify-center border border-gray-400 rounded text-xs font-medium text-gray-600">
                          {index + 1}
                        </div>
                      </div>
                      {/* Step + Expected side by side */}
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <div
                          className="border border-gray-200 rounded p-3 min-h-[80px] text-sm text-gray-700 bg-white"
                          dangerouslySetInnerHTML={{
                            __html:
                              step.step ||
                              '<span class="text-gray-400">—</span>',
                          }}
                        />
                        {testCaseTemplateType !== "BDD" && (
                          <div
                            className="border border-gray-200 rounded p-3 min-h-[80px] text-sm text-gray-700 bg-white"
                            dangerouslySetInnerHTML={{
                              __html:
                                step.expectedResult ||
                                '<span class="text-gray-400">—</span>',
                            }}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Edit mode: drag + quill editors + add/remove */
                <div className="space-y-4">
                  {formData?.testSteps?.map((step, index) => (
                    <div key={index}>
                      <div
                        className={`border rounded p-3 transition-all ${draggedStepIndex === index
                          ? "opacity-50 border-blue-400"
                          : dragOverStepIndex === index
                            ? "border-blue-500 border-2 bg-blue-50"
                            : "border-gray-300"
                          }`}
                      >
                        {step.isSharedStep ? <div style={{ textAlign: "right" }}>* Shared Step is not editable</div> : ""}
                        <div className="flex items-start gap-3">
                          {/* Drag handle */}
                          <div
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            className="flex-shrink-0 mt-1 cursor-grab active:cursor-grabbing"
                          >
                            <GripVertical className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                          </div>

                          {/* Step number circle */}
                          <div className="flex-shrink-0 mt-1 w-6 h-6 flex items-center justify-center border-2 border-gray-400 rounded-full">
                            <span className="text-xs font-medium text-gray-700">
                              {index + 1}
                            </span>
                          </div>

                          {/* Editors */}
                          <div className="flex-1 space-y-2">
                            {testCaseTemplateType === "BDD" && (
                              <select
                                value={step.gherkin_keyword || "Given"}
                                onChange={(e) =>
                                  handleStepChange(
                                    index,
                                    "gherkin_keyword",
                                    e.target.value,
                                  )
                                }
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              >
                                <option value="Given">Given</option>
                                <option value="When">When</option>
                                <option value="Then">Then</option>
                                <option value="And">And</option>
                                <option value="But">But</option>
                              </select>
                            )}
                            <div className="border border-gray-300 rounded overflow-hidden">
                              <ReactQuill
                                value={step.step}
                                onChange={(value) =>
                                  handleStepChange(index, "step", value)
                                }
                                modules={quillModules}
                                readOnly={step.isSharedStep}
                                formats={quillFormats}
                                className="min-h-[80px]"
                                placeholder="Details of the Steps"
                              />
                            </div>
                            {testCaseTemplateType !== "BDD" && (
                              <div className="border border-gray-300 rounded overflow-hidden">
                                <ReactQuill
                                  value={step.expectedResult}
                                  readOnly={step.isSharedStep}
                                  onChange={(value) =>
                                    handleStepChange(
                                      index,
                                      "expectedResult",
                                      value,
                                    )
                                  }
                                  modules={quillModules}
                                  formats={quillFormats}
                                  className="min-h-[80px]"
                                  placeholder="Details of the Expected Result"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Add / Remove row */}
                      {testCaseTemplateType !== "SINGLE_STEP" ? (
                        <div className="flex items-center justify-end gap-2 mt-1">
                          <button
                            type="button"
                            onClick={addStep}
                            className="w-5 h-5 flex items-center justify-center border-2 border-gray-400 text-gray-600 hover:border-gray-600 text-sm font-bold leading-none"
                          >
                            +
                          </button>
                          <button
                            type="button"
                            onClick={() => removeStep(index)}
                            className="w-5 h-5 flex items-center justify-center border-2 border-red-300 text-red-500 hover:border-red-500 text-sm font-bold leading-none"
                          >
                            −
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowSharedStepsModal(true);
                              setSharedStepInsertIndex(index + 1);

                            }}
                            className="text-sm font-medium text-gray-600 hover:text-gray-800"
                          >
                            + Add Step
                          </button>
                        </div>
                      ) :
                        <div className="flex items-center justify-end gap-2 mt-1">
                          <button
                            type="button"
                            onClick={() => {
                              setShowSharedStepsModal(true);
                              setSharedStepInsertIndex(index + 1);

                            }}
                            className="text-sm font-medium text-gray-600 hover:text-gray-800"
                          >
                            + Add Step
                          </button>
                        </div>
                      }

                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Attachment */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Attachement
              </label>

              {fieldsDisabled ? (
                /* View mode: compact thumbnail grid with paperclip icon */
                formData.attachments.length > 0 ? (
                  <div className="flex flex-wrap gap-3">
                    {formData.attachments.map((file, index) => {
                      const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(
                        file.file_name || "",
                      );
                      return (
                        <div
                          key={index}
                          className="relative bg-gray-100 rounded overflow-hidden flex-shrink-0"
                          style={{ width: 110, height: 80 }}
                        >
                          {isImage ? (
                            <img
                              src={file.file_url}
                              alt={file.file_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <Paperclip className="w-6 h-6" />
                            </div>
                          )}
                          {/* Paperclip badge top-right */}
                          <div className="absolute top-1 right-1 bg-white rounded p-0.5 shadow-sm">
                            <Paperclip className="w-3 h-3 text-gray-500" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No attachments</p>
                )
              ) : (
                /* Edit mode: upload button + cards with name/size/delete */
                <div className="bg-gray-100 rounded-lg p-4 flex flex-wrap gap-4 items-start">
                  {/* Upload button */}
                  <div className="flex flex-col items-start gap-1 flex-shrink-0">
                    <input
                      type="file"
                      id="file-upload"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      Upload File
                    </label>
                    <span className="text-xs text-gray-500">
                      Max. file size: 50 MB | Max. files: 10 (per upload)
                    </span>
                  </div>

                  {/* File cards */}
                  {formData.attachments.map((file, index) => {
                    const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(
                      file.file_name || "",
                    );
                    const truncatedName =
                      file.file_name && file.file_name.length > 14
                        ? file.file_name.substring(0, 11) + "..."
                        : file.file_name;
                    return (
                      <div
                        key={index}
                        className="flex flex-col items-center bg-white border border-gray-200 rounded-lg overflow-hidden flex-shrink-0"
                        style={{ width: 110 }}
                      >
                        {/* Thumbnail */}
                        <div
                          className="w-full bg-gray-50"
                          style={{ height: 80 }}
                        >
                          {isImage ? (
                            <img
                              src={file.file_url}
                              alt={file.file_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <Paperclip className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                        {/* Info row */}
                        <div className="w-full px-2 py-1.5 flex items-center justify-between gap-1">
                          <div className="flex flex-col min-w-0">
                            <span
                              className="text-xs text-gray-700 truncate"
                              title={file.file_name}
                            >
                              {truncatedName}
                            </span>
                            <span className="text-xs text-gray-400">
                              {(file.file_size / 1024).toFixed(0)} KB
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="flex-shrink-0 p-0.5 text-gray-400 hover:text-red-500 transition-colors"
                            title="Remove"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Comments */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Comments
              </label>

              {console.log({ savedComments })}
              {savedComments?.map((item) => {
                return (
                  <Stack gap={"4px"}>
                    <Typography color="var(--color-gray-500)">
                      {item.user.fullName}
                    </Typography>
                    <Typography
                      color="var(--color-gray-500)"
                      sx={{
                        padding: "0px 10px",
                        borderRadius: "6px",
                        border: "2px solid var(--color-gray-500)",
                      }}
                    >
                      {item.commentText}
                    </Typography>
                  </Stack>
                );
              })}
              <textarea
                name="comments"
                value={formData.comments}
                onChange={handleInputChange}
                rows={4}
                // disabled={fieldsDisabled}
                className={`w-full px-3 py-2  mt-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none ${fieldsDisabled ? "bg-gray-50 cursor-not-allowed" : ""}`}
                placeholder="Add Comment"
              />
            </div>
            <button
              type="button"
              onClick={() => addCommentData()}
              disabled={loading}
              className="px-5 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 font-medium"
            >
              Add Comment
            </button>

            <Stack sx={{ width: "max-content", flexDirection: "row" }}>
              {/* Bottom Action Buttons - Hidden in view mode */}
              {!viewMode ? (
                <div className="flex items-center gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => handleSubmit(false)}
                    disabled={loading}
                    className="px-5 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 font-medium"
                  >
                    {loading ? "Creating..." : "Create"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSubmit(true)}
                    disabled={loading}
                    className="px-5 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 font-medium"
                  >
                    Add & Create More
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={loading}
                    className="px-5 py-2 text-sm bg-white border border-gray-300 text-red-600 rounded hover:bg-gray-50 disabled:opacity-50 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3 pt-4">
                  {!isEditing ? (
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      disabled={loading}
                      className="px-5 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 font-medium"
                    >
                      Edit
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditing(false);
                        }}
                        disabled={loading}
                        className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSave(false)}
                        disabled={loading}
                        className="px-5 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 font-medium"
                      >
                        {loading ? "Saving..." : "Save"}
                      </button>
                    </>
                  )}
                </div>
              )}
            </Stack>
          </div>

          {console.log({ flattenedSections })}

          {/* Right Column - Metadata Panel */}
          <div className="w-72 flex-shrink-0">
            <div className="bg-gray-50 rounded border border-gray-200 p-4 sticky top-20">
              {fieldsDisabled ? (
                <div
                  className="space-y-4"
                  style={{
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Section */}
                  <Typography color="var(--color-gray-500)" fontSize="14px">
                    Section
                  </Typography>
                  <Typography color="var(--color-gray-700)" fontWeight={500}>
                    {formData.section_id
                      ? flattenedSections?.find(
                        (item) => item.value === formData.section_id,
                      ).label
                      : ""}
                  </Typography>
                  <Typography color="var(--color-gray-500)" fontSize="14px">
                    Template
                  </Typography>
                  <Typography color="var(--color-gray-700)" fontWeight={500}>
                    {testCaseTemplateType}
                  </Typography>

                  <Typography color="var(--color-gray-500)" fontSize="14px">
                    Approver
                  </Typography>
                  <Typography color="var(--color-gray-700)" fontWeight={500}>
                    {formData.reviewer_id}
                  </Typography>

                  <Typography color="var(--color-gray-500)" fontSize="14px">
                    Type
                  </Typography>
                  <Typography color="var(--color-gray-700)" fontWeight={500}>
                    {formData.type_of_test_case}
                  </Typography>

                  <Typography color="var(--color-gray-500)" fontSize="14px">
                    Priority
                  </Typography>
                  <Typography color="var(--color-gray-700)" fontWeight={500}>
                    {formData.priority}
                  </Typography>

                  <Typography color="var(--color-gray-500)" fontSize="14px">
                    Assigned To
                  </Typography>
                  <Typography color="var(--color-gray-700)" fontWeight={500}>
                    {formData.owner}
                  </Typography>

                  <Typography color="var(--color-gray-500)" fontSize="14px">
                    References
                  </Typography>
                  <Typography color="var(--color-gray-700)" fontWeight={500}>
                    {formData.tags}
                  </Typography>

                  <Typography color="var(--color-gray-500)" fontSize="14px">
                    Automation Type
                  </Typography>
                  <Typography color="var(--color-gray-700)" fontWeight={500}>
                    {formData.automation_type}
                  </Typography>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Section */}
                  <div>
                    <label
                      htmlFor="section_id"
                      className="block text-sm font-medium text-gray-900 mb-1.5"
                    >
                      Section <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="section_id"
                      name="section_id"
                      value={formData.section_id || ""}
                      onChange={handleInputChange}
                      disabled={fieldsDisabled || loadingSections}
                      className={`w-full px-2.5 py-2 text-sm border ${errors.section_id ? "border-red-500" : "border-gray-300"
                        } rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white ${fieldsDisabled ? "bg-gray-50 cursor-not-allowed" : ""}`}
                    >
                      <option value="">Manual Tests</option>
                      {flattenedSections.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.section_id && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.section_id}
                      </p>
                    )}
                  </div>

                  {/* Template */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      Template <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={testCaseTemplateType}
                      disabled
                      className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded bg-gray-100 text-gray-600 cursor-not-allowed"
                    >
                      <option value="SINGLE_STEP">Single Step</option>
                      <option value="MULTIPLE_STEPS">Test Case (Steps)</option>
                      <option value="BDD">BDD</option>
                    </select>
                  </div>

                  {/* Approver */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      Approver{" "}
                      {projectSettings.test_case_approval && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <select
                      name="reviewer_id"
                      value={formData.reviewer_id || ""}
                      onChange={handleInputChange}
                      disabled={fieldsDisabled || loadingProjectUsers}
                      className={`w-full px-2.5 py-2 text-sm border ${errors.reviewer_id
                        ? "border-red-500"
                        : "border-gray-300"
                        } rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white ${fieldsDisabled ? "bg-gray-50 cursor-not-allowed" : ""}`}
                    >
                      {approvers
                        .map((projectUser) => (
                          <option
                            key={projectUser.user_id}
                            value={projectUser.user_id}
                          >
                            {projectUser.full_name || projectUser.email}
                          </option>
                        ))}
                    </select>
                    {errors.reviewer_id && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.reviewer_id}
                      </p>
                    )}
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="type_of_test_case"
                      value={formData.type_of_test_case}
                      onChange={handleInputChange}
                      disabled={fieldsDisabled}
                      className={`w-full px-2.5 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white ${fieldsDisabled ? "bg-gray-50 cursor-not-allowed" : ""}`}
                    >
                      <option value="functional">Functional</option>
                      <option value="other">Other</option>
                      <option value="security">Security</option>
                      <option value="usability">Usability</option>
                      <option value="smoke">Smoke</option>
                      <option value="sanity">Sanity</option>
                      <option value="regression">Regression</option>
                      <option value="performance">Performance</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      Priority <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      disabled={fieldsDisabled}
                      className={`w-full px-2.5 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white ${fieldsDisabled ? "bg-gray-50 cursor-not-allowed" : ""}`}
                    >
                      <option value="medium">Medium</option>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="low">Low</option>
                    </select>
                  </div>

                  {/* Assigned To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Assigned To
                    </label>
                    <select
                      name="owner"
                      value={formData.owner || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                      disabled={loadingProjectUsers}
                    >
                      <option value="">Unassigned</option>
                      {projectUsers.map((user) => (
                        <option key={user.user_id} value={user.user_id}>
                          {user.full_name || user.email}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* References */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      References
                    </label>
                    <input
                      type="text"
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      placeholder=""
                      disabled={fieldsDisabled}
                      className={`w-full px-2.5 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${fieldsDisabled ? "bg-gray-50 cursor-not-allowed" : ""}`}
                    />
                  </div>

                  {/* Automation Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      Automation Type
                    </label>
                    <select
                      name="automation_type"
                      value={formData.automation_type || ""}
                      onChange={handleInputChange}
                      disabled={fieldsDisabled}
                      className={`w-full px-2.5 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white ${fieldsDisabled ? "bg-gray-50 cursor-not-allowed" : ""}`}
                    >
                      <option value="">Appium</option>
                      <option value="record_and_play">Record and Play</option>
                      <option value="self_automated">Self Automated</option>
                    </select>
                  </div>

                  {/* Is Automated */}
                  <div>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.automation_status === "automated"}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            automation_status: e.target.checked
                              ? "automated"
                              : "not_automated",
                          }))
                        }
                        disabled={fieldsDisabled}
                        className={`mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${fieldsDisabled ? "cursor-not-allowed" : ""}`}
                      />
                      <span className="text-xs text-gray-600">
                        Indicates test automated by script.
                      </span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestCaseCreatePage;
